import React, { useEffect } from 'react';
import {
    Grid,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Box,
    Typography,
    Paper,
    Checkbox,
    FormControlLabel,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import ShareIcon from '@mui/icons-material/Share';
import LinkIcon from '@mui/icons-material/Link';
import ImageIcon from '@mui/icons-material/Image';
import { useClientsManagementController } from "../context/clients-management-controller";
import { useClientsManagementState } from "../context/clients-management-context";
import MaskedTextField from '../../../components/masked-text-field';
import DefaultUploadFile from '../../../components/default-upload-file';

const ClientsManagementForm: React.FC = () => {
    const { handleFormChange, handleAddressChange, handleContactChange, loadStates, loadCities, updateAddress, setBrandLogo } = useClientsManagementController();
    const { clientForm, listStateResponse, listCityResponse, brandLogo } = useClientsManagementState();

    const handleFieldChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
        handleFormChange(field, event.target.value);
    };

    const handleSelectChange = (field: string) => (event: any) => {
        handleFormChange(field, event.target.value);
    };

    const handleCheckboxChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
        handleFormChange(field, event.target.checked);
    };

    const handleAddressSelectChange = (field: string) => (event: any) => {
        handleAddressChange(field, event.target.value);
    };

    const handleAddressFieldChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
        handleAddressChange(field, event.target.value);
    };

    const handleContactFieldChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
        handleContactChange(field, event.target.value);
    };

    const handleStateChange = (event: any) => {
        const stateId = Number(event.target.value);

        updateAddress({
            stateId: stateId,
            cityId: 0
        });

        if (stateId && stateId !== 0) {
            loadCities(stateId);
        }
    };

    useEffect(() => {
        loadStates();
    }, []);

    const SectionTitle: React.FC<{ icon: React.ReactNode; title: string }> = ({ icon, title }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            {icon}
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                {title}
            </Typography>
        </Box>
    );

    return (
        <Box sx={{ px: 1 }}>
            <Grid container spacing={3}>
                {/* Informações Básicas */}
                <Grid item xs={12}>
                    <Paper elevation={0} sx={{ p: 3, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
                        <SectionTitle icon={<BusinessIcon color="primary" />} title="Informações Básicas" />
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Nome"
                                    value={clientForm?.name || ''}
                                    onChange={handleFieldChange('name')}
                                    required
                                    placeholder="Digite o nome da entidade"
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Razão Social"
                                    value={clientForm?.corporateName || ''}
                                    onChange={handleFieldChange('corporateName')}
                                    required
                                    placeholder="Digite a razão social"
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <MaskedTextField
                                    maskType="cnpj"
                                    label="CNPJ"
                                    id="input-document"
                                    name="input-document"
                                    value={clientForm?.document || ''}
                                    onChange={handleFieldChange('document')}
                                    fullWidth
                                    required
                                    placeholder="00.000.000/0000-00"
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth required>
                                    <InputLabel>Tipo de Cliente</InputLabel>
                                    <Select
                                        value={clientForm?.entityType || 0}
                                        onChange={handleSelectChange('entityType')}
                                        label="Tipo de Cliente"
                                    >
                                        <MenuItem value={0}>
                                            <em>Selecione um tipo</em>
                                        </MenuItem>
                                        <MenuItem value={1}>Prefeitura Municipal</MenuItem>
                                        <MenuItem value={2}>Câmara Municipal</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={12}>
                                <TextField
                                    fullWidth
                                    label="Website"
                                    value={clientForm?.webPage || ''}
                                    onChange={handleFieldChange('webPage')}
                                    type="url"
                                    placeholder="https://exemplo.com.br"
                                />
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>

                {/* Logomarca */}
                <Grid item xs={12}>
                    <Paper elevation={0} sx={{ p: 3, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
                        <SectionTitle icon={<ImageIcon color="primary" />} title="Logomarca" />
                        <Box sx={{ mb: 1 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Envie a logomarca da entidade (formato PNG, máximo 2MB)
                            </Typography>
                            <DefaultUploadFile
                                id="upload-brand-logo"
                                setFileUpload={setBrandLogo}
                                file={brandLogo ?? null}
                                extensions={["png"]}
                                maxFileSizeMb={2}
                            />
                        </Box>
                    </Paper>
                </Grid>

                {/* Redes Sociais */}
                <Grid item xs={12}>
                    <Paper elevation={0} sx={{ p: 3, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
                        <SectionTitle icon={<ShareIcon color="primary" />} title="Redes Sociais" />
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Instagram"
                                    value={clientForm?.instagramProfile || ''}
                                    onChange={handleFieldChange('instagramProfile')}
                                    placeholder="@perfil ou URL completa"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Facebook"
                                    value={clientForm?.facebookProfile || ''}
                                    onChange={handleFieldChange('facebookProfile')}
                                    placeholder="@perfil ou URL completa"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Twitter"
                                    value={clientForm?.twitterProfile || ''}
                                    onChange={handleFieldChange('twitterProfile')}
                                    placeholder="@perfil ou URL completa"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="YouTube"
                                    value={clientForm?.youtubeProfile || ''}
                                    onChange={handleFieldChange('youtubeProfile')}
                                    placeholder="Canal ou URL completa"
                                />
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>

                {/* Links de Serviços */}
                <Grid item xs={12}>
                    <Paper elevation={0} sx={{ p: 3, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
                        <SectionTitle icon={<LinkIcon color="primary" />} title="Links de Serviços" />
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="URL Servidores"
                                    value={clientForm?.urlServidores || ''}
                                    onChange={handleFieldChange('urlServidores')}
                                    type="url"
                                    placeholder="https://"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="URL Transparência"
                                    value={clientForm?.urlTransparencia || ''}
                                    onChange={handleFieldChange('urlTransparencia')}
                                    type="url"
                                    placeholder="https://"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="URL Ouvidoria"
                                    value={clientForm?.urlOuvidoria || ''}
                                    onChange={handleFieldChange('urlOuvidoria')}
                                    type="url"
                                    placeholder="https://"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="URL Licitações"
                                    value={clientForm?.urlLicitacoes || ''}
                                    onChange={handleFieldChange('urlLicitacoes')}
                                    type="url"
                                    placeholder="https://"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="URL Contratos"
                                    value={clientForm?.urlContratos || ''}
                                    onChange={handleFieldChange('urlContratos')}
                                    type="url"
                                    placeholder="https://"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="URL Nota Fiscal"
                                    value={clientForm?.urlNotaFiscal || ''}
                                    onChange={handleFieldChange('urlNotaFiscal')}
                                    type="url"
                                    placeholder="https://"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="URL SIC"
                                    value={clientForm?.urlSic || ''}
                                    onChange={handleFieldChange('urlSic')}
                                    type="url"
                                    placeholder="https://"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="URL Diário Oficial"
                                    value={clientForm?.urlDiarioOficial || ''}
                                    onChange={handleFieldChange('urlDiarioOficial')}
                                    type="url"
                                    placeholder="https://"
                                />
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>

                {/* Endereço */}
                <Grid item xs={12}>
                    <Paper elevation={0} sx={{ p: 3, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
                        <SectionTitle icon={<LocationOnIcon color="primary" />} title="Endereço" />
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={9}>
                                <TextField
                                    fullWidth
                                    label="Logradouro"
                                    value={clientForm?.address?.street || ''}
                                    onChange={handleAddressFieldChange('street')}
                                    placeholder="Rua, Avenida, etc."
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    label="Número"
                                    value={clientForm?.address?.number || ''}
                                    onChange={handleAddressFieldChange('number')}
                                    placeholder="Nº"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Complemento"
                                    value={clientForm?.address?.complement || ''}
                                    onChange={handleAddressFieldChange('complement')}
                                    placeholder="Apto, Sala, etc."
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Bairro"
                                    value={clientForm?.address?.neighborhood || ''}
                                    onChange={handleAddressFieldChange('neighborhood')}
                                    placeholder="Nome do bairro"
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <MaskedTextField
                                    maskType="cep"
                                    label="CEP"
                                    id="input-postalCode"
                                    name="input-postalCode"
                                    value={clientForm?.address?.postalCode || ''}
                                    onChange={handleAddressFieldChange('postalCode')}
                                    fullWidth
                                    placeholder="00000-000"
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth>
                                    <InputLabel>Estado</InputLabel>
                                    <Select
                                        value={clientForm?.address?.stateId || 0}
                                        onChange={handleStateChange}
                                        label="Estado"
                                    >
                                        <MenuItem value={0}>
                                            <em>Selecione um estado</em>
                                        </MenuItem>
                                        {listStateResponse?.map((state) => (
                                            <MenuItem key={state.id} value={state.id}>
                                                {state.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth>
                                    <InputLabel>Cidade</InputLabel>
                                    <Select
                                        value={clientForm?.address?.cityId || 0}
                                        onChange={handleAddressSelectChange('cityId')}
                                        label="Cidade"
                                        disabled={!clientForm?.address?.stateId || clientForm?.address?.stateId === 0}
                                    >
                                        <MenuItem value={0}>
                                            <em>
                                                {!clientForm?.address?.stateId || clientForm?.address?.stateId === 0
                                                    ? "Selecione um estado primeiro"
                                                    : "Selecione uma cidade"}
                                            </em>
                                        </MenuItem>
                                        {listCityResponse?.map((city) => (
                                            <MenuItem key={city.id} value={city.id}>
                                                {city.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>

                {/* Contato */}
                <Grid item xs={12}>
                    <Paper elevation={0} sx={{ p: 3, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
                        <SectionTitle icon={<ContactMailIcon color="primary" />} title="Contato" />
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="E-mail"
                                    type="email"
                                    value={clientForm?.contact?.email || ''}
                                    onChange={handleContactFieldChange('email')}
                                    placeholder="contato@exemplo.com.br"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <MaskedTextField
                                    maskType="phone"
                                    label="Telefone"
                                    id="input-phone"
                                    name="input-phone"
                                    fullWidth
                                    value={clientForm?.contact?.phone || ''}
                                    onChange={handleContactFieldChange('phone')}
                                    placeholder="(00) 00000-0000"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Website de Contato"
                                    value={clientForm?.contact?.website || ''}
                                    onChange={handleContactFieldChange('website')}
                                    type="url"
                                    placeholder="https://exemplo.com.br"
                                />
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default ClientsManagementForm;
